import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";

// ######### MATERIAL UI AND LAYOUT COMPONENTS
import { Typography, makeStyles, Box, Button } from "@material-ui/core";
import BusinessIcon from "@material-ui/icons/Business";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import ReceiptIcon from "@material-ui/icons/Receipt";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import FunctionsIcon from "@material-ui/icons/Functions";
import AssistantIcon from "@material-ui/icons/Assistant";
import LinkIcon from "@material-ui/icons/Link";
import GavelIcon from "@material-ui/icons/Gavel";
import { ThreeDots } from "react-loader-spinner";

// ######### CUSTOM COMPONENTS
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardInput from "./CardInput";
import DialogRelationships from "../DialogRelationships/DialogRelationships";
import DialogPremises from "../DialogPremises/DialogPremises";
import DialogModelingAssistant from "../DialogModelingAssistant/DialogModelingAssistant";

// ######### CONTEXT
import AuthContext from "connections/auth/AuthProvider.js";
import { useTranslation } from "react-i18next";

// ######### CONSTANTS
import { isInputNotApplicable } from "services/utils/calculationConstants";

// ######### STYLES
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";

const navigationSideMenuStyle = (theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1), // Use theme spacing to work with GridContainer
    height: "calc(100vh - 110px)", // Reduced to minimize top spacing
    paddingRight: 0, // Remove padding to align with horizontal cards
  },
  loadingBox: {
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  // Top horizontal section for group_l1="1"
  topSection: {
    margin: 0,
  },
  topSectionTitle: {
    textAlign: "center",
    padding: "8px 16px",
    fontWeight: 400,
    fontSize: "0.875rem",
    backgroundColor: "#FAFAFA",
    color: (props) => props.clientColor || "#1976d2",
    borderRadius: "5px 5px 0 0",
    border: `1px solid ${grayColor[11]}`,
    borderBottom: (props) => `1px solid ${props.clientColor || "#1976d2"}`,
    textTransform: "capitalize",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5), // Reduced from 1 to 0.5 (4px instead of 8px)
  },
  topSectionTitleText: {
    fontWeight: 400,
    //flex: 1,
    textAlign: "center",
  },
  topSectionIcon: {
    fontSize: "1rem",
    opacity: 0.7,
    color: (props) => props.clientColor || "#1976d2",
  },
  topList: {
    display: "flex",
    flexDirection: "row",
    overflowX: "auto",
    gap: theme.spacing(2),
  },
  topSectionButtonsContainer: {
    position: "absolute",
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  topSectionButton: {
    fontSize: "0.75rem",
    padding: theme.spacing(0.5, 1.5),
    minHeight: "32px",
    textTransform: "none",
    borderRadius: "4px",
    border: `1px solid ${grayColor[8]}`,
    backgroundColor: "white",
    color: grayColor[2],
    "&:hover": {
      backgroundColor: (props) => `${props.clientColor || "#1976d2"}08`,
      borderColor: (props) => props.clientColor || "#1976d2",
      color: (props) => props.clientColor || "#1976d2",
    },
  },
  // Bottom columnar section for group_l1="2,3,4,5"
  columnarSection: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2), // Increased spacing between columns
    overflowX: "auto",
    flex: 1, // Take remaining height in container
    alignItems: "stretch", // Make columns stretch to full height
    width: "100%", // Occupy full width
  },
  column: {
    margin: 0,
    minWidth: "220px", // Minimum width to maintain readability
    flex: 1, // Allow columns to grow and share available space equally
    height: "95%", // Fill available height from parent flex container
    display: "flex",
    flexDirection: "column",
    overflow: "hidden", // Prevent column overflow
    transition: "all 0.3s ease", // Smooth transition when columns resize
  },
  columnTitle: {
    textAlign: "center",
    padding: "8px 16px",
    fontWeight: 400,
    fontSize: "0.875rem",
    backgroundColor: "#FAFAFA",
    color: (props) => props.clientColor || "#1976d2",
    borderRadius: "5px 5px 0 0",
    border: `1px solid ${grayColor[11]}`,
    borderBottom: (props) => `1px solid ${props.clientColor || "#1976d2"}`,
    textTransform: "capitalize",
    margin: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5), // Consistent with topSectionTitle
  },
  columnTitleText: {
    fontWeight: 400,
    textAlign: "center",
  },
  columnIcon: {
    fontSize: "1rem",
    opacity: 0.7,
    color: (props) => props.clientColor || "#1976d2",
  },
  clientLogo: {
    height: "18px",
    width: "auto",
    maxWidth: "80px",
    opacity: 0.6,
    filter: "grayscale(80%) brightness(1.1)",
    objectFit: "contain",
    display: "block",
  },
  columnContent: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", // Responsive grid
    gap: theme.spacing(1.5),
    overflowY: "auto",
    flex: 1,
    padding: `${theme.spacing(1)}px ${theme.spacing(1)}px`,
    alignContent: "start", // Align items to top
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "3px",
      "&:hover": {
        background: "#555",
      },
    },
  },
});

const useStyles = makeStyles(navigationSideMenuStyle);

// ######### ICON HELPER (exported for DialogRelationships)
export const getIconForGroup = (
  groupName,
  classes,
  userDetails,
  isTopSection = false,
) => {
  const lowerName = groupName.toLowerCase();

  // Top section (horizontal) - modeling/calculations icon
  if (isTopSection) {
    return <AccountTreeIcon className={classes.topSectionIcon} />;
  }

  // Model group - fallback for top section
  if (lowerName.includes("model")) {
    return <AccountTreeIcon className={classes.topSectionIcon} />;
  }

  // Internal group - factory icon with client logo if available
  if (lowerName.includes("internal")) {
    // Try to use client logo if available (supports both URLs and data URIs)
    const logoSrc = userDetails?.implementation_logo;
    if (
      logoSrc &&
      (logoSrc.startsWith("http") || logoSrc.startsWith("data:image"))
    ) {
      return (
        <img
          src={logoSrc}
          alt=""
          className={classes.clientLogo}
          onError={(e) => {
            // Hide image if it fails to load
            e.target.style.display = "none";
          }}
        />
      );
    }
    return <BusinessIcon className={classes.columnIcon} />; // Fallback to factory/business icon
  }

  // Taxes group - receipt/tax icon
  if (lowerName.includes("tax")) {
    return <ReceiptIcon className={classes.columnIcon} />;
  }

  // Chain group - flow/process chain icon
  if (lowerName.includes("chain")) {
    return <LocalShippingIcon className={classes.columnIcon} />;
  }

  // Market group - trending icon
  if (lowerName.includes("market") || lowerName.includes("trend")) {
    return <TrendingUpIcon className={classes.columnIcon} />;
  }

  // Default icon (calculations)
  return <FunctionsIcon className={classes.columnIcon} />;
};

// ######### METADATA SANITIZATION
function sanitize_metadata(inputMetadata) {
  const hierarchicalGroups = {};

  // First, collect all tables with their grouping information
  const tablesWithGroups = [];
  for (const tableId in inputMetadata) {
    const table = inputMetadata[tableId];

    // Filter: Only render tables with group_level3 === 'file_inputs'
    if (table.group_level3 !== "file_inputs") {
      continue;
    }

    tablesWithGroups.push({
      id: table.table_id,
      name: table.table_name,
      desc: table.table_description,
      ready: table.table_ready,
      group_l1: table.group_level1 || "999", // Use correct field name from backend
      group_l2: table.group_level2 || "Ungrouped", // Use correct field name from backend
      table_order: table.table_order || 999, // Default to high number for unordered
    });
  }

  // Sort tables by group_l1 first, then by table_order within each group
  tablesWithGroups.sort((a, b) => {
    // First sort by group_l1 (primary grouping)
    const group1Compare = parseInt(a.group_l1) - parseInt(b.group_l1);
    if (group1Compare !== 0) {
      return group1Compare;
    }
    // Then sort by table_order within the same group
    return parseInt(a.table_order) - parseInt(b.table_order);
  });

  // Group hierarchically: first by group_l1, then by group_l2
  for (const table of tablesWithGroups) {
    const l1Key = table.group_l1;
    const l2Key = table.group_l2;

    if (!hierarchicalGroups[l1Key]) {
      hierarchicalGroups[l1Key] = {};
    }

    if (!hierarchicalGroups[l1Key][l2Key]) {
      hierarchicalGroups[l1Key][l2Key] = [];
    }

    hierarchicalGroups[l1Key][l2Key].push({
      id: table.id,
      name: table.name,
      desc: table.desc,
      ready: table.ready,
    });
  }

  return hierarchicalGroups;
}

// ######### MAIN COMPONENT
export default function PanelInputs({
  selectedInputTable,
  setSelectedInputTable,
  inputMetadata,
  inputColumns,
  isLoadingMetadata,
  selectedCalculationMethod,
}) {
  const { userDetails } = useContext(AuthContext);
  const classes = useStyles({ clientColor: userDetails.implementation_color });
  const { t } = useTranslation("translation", { keyPrefix: "rgm.inputs" });
  const [structuredMetadata, setStructuredMetadata] = useState({});
  const [dialogRelationshipsOpen, setDialogRelationshipsOpen] = useState(false);
  const [dialogModelingAssistantOpen, setDialogModelingAssistantOpen] =
    useState(false);
  const [dialogPremisesOpen, setDialogPremisesOpen] = useState(false);

  useEffect(() => {
    const groupedData = sanitize_metadata(inputMetadata);
    setStructuredMetadata(groupedData);
  }, [inputMetadata]);

  const handleItemClick = (item) => {
    setSelectedInputTable(item.id);
  };

  if (isLoadingMetadata) {
    return (
      <Box className={classes.loadingBox}>
        <ThreeDots
          color={userDetails.implementation_color}
          ariaLabel="loading"
        />
      </Box>
    );
  }

  // Separate top section (group_l1="1") from columnar sections
  const topSectionData = structuredMetadata["1"] || {};
  const columnarSectionData = {};

  // Get columnar sections ordered by group_l1
  const sortedL1Keys = Object.keys(structuredMetadata)
    .filter((key) => key !== "1")
    .sort((a, b) => parseInt(a) - parseInt(b));

  sortedL1Keys.forEach((l1Key) => {
    Object.entries(structuredMetadata[l1Key]).forEach(([group2Name, items]) => {
      columnarSectionData[group2Name] = items;
    });
  });

  return (
    <Box className={classes.container}>
      {/* Top Horizontal Section for group_l1="1" */}
      {Object.keys(topSectionData).length > 0 &&
        Object.entries(topSectionData).map(([group2Name, items]) => {
          return (
            <Card key={group2Name} className={classes.topSection}>
              <Box className={classes.topSectionTitle}>
                {getIconForGroup(group2Name, classes, userDetails, true)}
                <Typography className={classes.topSectionTitleText}>
                  {group2Name}
                </Typography>
                <Box className={classes.topSectionButtonsContainer}>
                  <Button
                    className={classes.topSectionButton}
                    startIcon={<AssistantIcon />}
                    onClick={() => setDialogModelingAssistantOpen(true)}
                  >
                    {t("modeling_assistant")}
                  </Button>
                  <Button
                    className={classes.topSectionButton}
                    startIcon={<LinkIcon />}
                    onClick={() => setDialogRelationshipsOpen(true)}
                  >
                    {t("manage_relationships")}
                  </Button>
                  <Button
                    className={classes.topSectionButton}
                    startIcon={<GavelIcon />}
                    onClick={() => setDialogPremisesOpen(true)}
                    style={{ marginRight: "12px" }}
                  >
                    {t("manage_premises")}
                  </Button>
                </Box>
              </Box>
              <CardBody className={classes.topList}>
                {items.map((item) => {
                  const isSelected = selectedInputTable === item.id;
                  const isNotApplicable = isInputNotApplicable(
                    item.id,
                    selectedCalculationMethod,
                  );
                  return (
                    <CardInput
                      key={item.id}
                      item={item}
                      isSelected={isSelected}
                      onClick={() => handleItemClick(item)}
                      clientColor={userDetails.implementation_color}
                      isHorizontalSection={true}
                      isNotApplicable={isNotApplicable}
                    />
                  );
                })}
              </CardBody>
            </Card>
          );
        })}

      {/* Bottom Columnar Section for group_l1="2,3,4,5" */}
      {Object.keys(columnarSectionData).length > 0 && (
        <Box className={classes.columnarSection}>
          {Object.entries(columnarSectionData).map(([group2Name, items]) => (
            <Card key={group2Name} className={classes.column}>
              <Box className={classes.columnTitle}>
                {getIconForGroup(group2Name, classes, userDetails)}
                <Typography className={classes.columnTitleText}>
                  {group2Name}
                </Typography>
              </Box>
              <CardBody className={classes.columnContent}>
                {items.map((item) => {
                  const isSelected = selectedInputTable === item.id;
                  const isNotApplicable = isInputNotApplicable(
                    item.id,
                    selectedCalculationMethod,
                  );
                  return (
                    <CardInput
                      key={item.id}
                      item={item}
                      isSelected={isSelected}
                      onClick={() => handleItemClick(item)}
                      clientColor={userDetails.implementation_color}
                      isNotApplicable={isNotApplicable}
                    />
                  );
                })}
              </CardBody>
            </Card>
          ))}
        </Box>
      )}

      {/* Dialog Premises */}
      <DialogPremises
        open={dialogPremisesOpen}
        onClose={() => setDialogPremisesOpen(false)}
      />

      {/* Dialog Relationships */}
      <DialogRelationships
        open={dialogRelationshipsOpen}
        onClose={() => setDialogRelationshipsOpen(false)}
        structuredMetadata={structuredMetadata}
        getIconForGroup={getIconForGroup}
        inputColumns={inputColumns}
        userDetails={userDetails}
      />

      {/* Dialog Modeling Assistant */}
      <DialogModelingAssistant
        open={dialogModelingAssistantOpen}
        onClose={() => setDialogModelingAssistantOpen(false)}
        inputMetadata={inputMetadata}
      />
    </Box>
  );
}

// ######### PROP TYPES
PanelInputs.propTypes = {
  selectedInputTable: PropTypes.string.isRequired,
  setSelectedInputTable: PropTypes.func.isRequired,
  inputMetadata: PropTypes.object.isRequired,
  inputColumns: PropTypes.object.isRequired,
  isLoadingMetadata: PropTypes.bool,
  selectedCalculationMethod: PropTypes.string,
};
